import { solutionScopeOf } from '../../utils/solution-version-id';
import { reportProgress, type Translate } from './knowledge-setup-text';
import {
  RAG_SEARCH_COMMAND,
  type KnowledgeProgress,
  type KnowledgeSetupDeps,
} from './run-knowledge-setup';

/** The fields of a table row this wiring reads. */
export interface KnowledgeSetupRow {
  id: string;
  command?: string | null;
  parameterInput?: unknown;
}

/**
 * What `useKnowledgeSetup` hands in: readers of the app's live state and the
 * functions that act on it. Every reader is called when a step runs, never
 * cached, so the wiring sees the state as it is at that moment.
 */
export interface KnowledgeSetupWiring {
  /** `ctx.selectedBot.uniqueId` at the moment the run starts. */
  solutionKey: string;
  /** `ctx.selectedBot.uniqueId` now. */
  readOpenSolutionKey: () => string | undefined;
  /** `tableStore.solutionVersionId` now. */
  readStoreSolutionVersionId: () => string;
  /** The table store's rows now. */
  readRows: () => readonly KnowledgeSetupRow[];
  updateRow: (args: {
    rowId: string;
    updates: { parameterInput: Record<string, unknown> };
    saveImmediately: true;
  }) => void;
  /**
   * `POST <uploadUrl>` of one file. A callback fires when the request answers
   * or fails; one that never does is what `UPLOAD_TIMEOUT_MS` bounds, using the
   * returned `abort` to cancel the request.
   */
  upload: (args: {
    url: string;
    file: File;
    onDone: (data: unknown) => void;
    onError: (err: unknown) => void;
  }) => { abort?: () => void } | void;
  /** The draft version's script endpoint, built from the ids the run started with. */
  uploadUrl: string;
  /** `updateDraftData` with the draft an upload answered with. */
  dispatchDraft: (draft: unknown) => void;
  readScript: KnowledgeSetupDeps['readScript'];
  post: KnowledgeSetupDeps['post'];
  t: Translate;
  isToastActive: (toastId: string) => boolean;
  sendToast: Parameters<typeof reportProgress>[1]['send'];
  updateToast: Parameters<typeof reportProgress>[1]['update'];
}

/**
 * How long one script upload may go unanswered. `XMLHttpRequest` has no
 * timeout of its own, so a stalled connection fired neither callback and the
 * run awaited for good behind a progress toast that never closes
 * (PSTUDIO-4458). The files are small (a key, one script), so a minute is
 * generous.
 */
export const UPLOAD_TIMEOUT_MS = 60_000;

export type UploadFileFn = (args: {
  url: string;
  file: File[];
  type: 'scriptFile';
  onChange: (result: { data?: unknown }) => void;
  error: (err: unknown) => void;
}) => { abort?: () => void } | void;

/**
 * The wiring `use-knowledge-setup.ts` hands `uploadFile` through, kept here
 * because the hook cannot be rendered in a unit test. It returns what
 * `uploadFile` returns: that handle is how `UPLOAD_TIMEOUT_MS` aborts a
 * request that never answers.
 */
export const adaptUploadFile =
  (uploadFile: UploadFileFn): KnowledgeSetupWiring['upload'] =>
  ({ url, file, onDone, onError }) =>
    uploadFile({
      url,
      file: [file],
      type: 'scriptFile',
      onChange: ({ data }) => onDone(data),
      error: onError,
    });

/**
 * The deps `runKnowledgeSetup` runs with, built from the app's live state.
 * Call it when the run starts: that is when the store identity is captured.
 *
 * Two questions, because leaving is not switching. `isSameSolution` is the
 * strict one (both identities still match) and guards what writes onto the open
 * solution's rows; `isAnotherSolutionOpen` is true only when a different
 * solution is known to be open, and guards the uploads, which are addressed by
 * the ids the run started with and so survive a mere leave (All Solutions,
 * Configurations, Talk: the machine's `selectedBot` is cleared, the store may
 * hold `''`).
 *
 * "The same solution" needs BOTH identities to still match. The machine's
 * (`ctx.selectedBot.uniqueId`) lags on a Back/Forward between solutions, where
 * only `botName` moves (canon `solution-identity-ctx`); the table store's is
 * what `setCorpusId` actually writes under, and its solution part is compared
 * through `solutionScopeOf`, so a publish that bumps the draft version of the
 * same solution does not stop the run. A store holding no solution at the start
 * never counts as the same, since `''` is also what it holds outside a solution.
 * LIMITATION: a store that lets go of the solution for a moment (re-entering
 * it passes through `''`, `table-store-route-sync.ts`) stops a run whose check
 * lands in that moment.
 */
export const createKnowledgeSetupDeps = (
  wiring: KnowledgeSetupWiring
): KnowledgeSetupDeps => {
  const startScope = solutionScopeOf(wiring.readStoreSolutionVersionId());
  const isSameSolution = () =>
    startScope !== '' &&
    solutionScopeOf(wiring.readStoreSolutionVersionId()) === startScope &&
    wiring.readOpenSolutionKey() === wiring.solutionKey;
  // A DIFFERENT solution is known to be open: the machine names one other than
  // the start's, or the store holds a solution scope other than the start's. An
  // absent answer (`undefined`, `''`) is "none open", which is not this.
  const isAnotherSolutionOpen = () => {
    const openKey = wiring.readOpenSolutionKey();
    const storeScope = solutionScopeOf(wiring.readStoreSolutionVersionId());
    return (
      (openKey !== undefined && openKey !== wiring.solutionKey) ||
      (storeScope !== '' && storeScope !== startScope)
    );
  };

  const searchNode = () =>
    wiring.readRows().find(
      (r) =>
        r.command === RAG_SEARCH_COMMAND ||
        // The AI-Native template's search (`ai-native-swap.ts`).
        r.command === `AsyncSys${RAG_SEARCH_COMMAND}`
    );

  return {
    progress: (step: KnowledgeProgress) =>
      reportProgress(step, {
        t: wiring.t,
        isActive: wiring.isToastActive,
        send: wiring.sendToast,
        update: wiring.updateToast,
      }),
    isSameSolution,
    isAnotherSolutionOpen,
    hasSearchNode: () => !!searchNode(),
    uploadScript: (file) =>
      new Promise<void>((resolve, reject) => {
        // The first of answer, failure and timeout wins; the others are
        // ignored, so a late answer after the bound hands nothing to the
        // machine. `abort()` makes the request fire `onError` itself.
        let settled = false;
        // An object, not a `const` timer: `upload` may answer synchronously,
        // so `settle` can run before the timer exists.
        const timer: { id?: ReturnType<typeof setTimeout> } = {};
        const settle = (finish: () => void) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer.id);
          finish();
        };
        const request = wiring.upload({
          url: wiring.uploadUrl,
          file,
          onDone: (data) =>
            settle(() => {
              // The draft belongs to the solution the run started in; handed
              // to the machine after a switch it would become the OTHER
              // solution's draft and `draftVersion`, under a "Script uploaded"
              // toast. It still resolves, and the runner's check before its
              // next step reports the switch. When this upload was the run's
              // LAST step (no references attached), nothing asks again and the
              // run reports success — which the upload, made under the
              // original ids, is.
              if (data && isSameSolution()) wiring.dispatchDraft(data);
              resolve();
            }),
          onError: (err) =>
            settle(() => {
              // The raw answer is logged here and becomes the failure's
              // `cause`; the user text does not show it for an upload, it names
              // the file from a key (`failedUpload`, `knowledge-setup-text.ts`).
              console.error(
                `[knowledge-setup] uploading ${file.name} failed:`,
                err
              );
              reject(new Error(String(err)));
            }),
        });
        // Armed after `upload`, which may answer synchronously: `settle` then
        // has already run and the timer must not be started at all.
        if (settled) return;
        timer.id = setTimeout(
          () =>
            settle(() => {
              request?.abort?.();
              console.error(
                `[knowledge-setup] uploading ${file.name}: no answer in ${UPLOAD_TIMEOUT_MS / 1000}s`
              );
              reject(
                new Error(
                  `The server did not answer in ${UPLOAD_TIMEOUT_MS / 1000}s.`
                )
              );
            }),
          UPLOAD_TIMEOUT_MS
        );
      }),
    readScript: wiring.readScript,
    post: wiring.post,
    setCorpusId: (corpusId) => {
      const node = searchNode();
      if (!node) return false;
      const params =
        typeof node.parameterInput === 'string'
          ? JSON.parse(node.parameterInput || '{}')
          : { ...((node.parameterInput as object | null) ?? {}) };
      wiring.updateRow({
        rowId: node.id,
        updates: {
          parameterInput: {
            ...params,
            live_corpus_id: corpusId,
            sandbox_corpus_id: corpusId,
          },
        },
        saveImmediately: true,
      });
      return true;
    },
  };
};
